import * as React from 'react';
import authService from './api-authorization/AuthorizeService';
import axios from 'axios';
import { toast } from 'react-toastify';
import PhotoViewerModal from './PhotoViewer';
import EditNoteModal from './EditNotes';
import * as DateHelpers from './DateHelpers';
import { Link } from 'react-router-dom';

interface RunSheetDriverPlanProps {
    systemCompanyId: number;
    runsheetId: number;
    jobId: number;
    itemOptions: ItemOptionList[];
    save(): void;
}

interface Job {
    runsheetId: number;
    scheduleBranchId: number;
    scheduleDate: string;
    scheduleDateFormatted: string;
    onHireDate: string;
    truck: string;
    truckId: number;
    jobId: number;
    siteAddress: string;
    customerName: string;
    customerAccountType: string;
    updateTypeId: number;     //1=Callout, 2=Plan Changes
    estimatedTime: string;
    departureTime: string;
    siteArrivalTime: string;
    downTime: string;
    siteCompletionTime: string;
    driverNotes: string;
    chargeCalloutFee: number;
    noCalloutInvoiceNotes: string;
    calloutNotes: string;
    planChangesReason: string;
    officeNotes: string;
    items: JobItem[];
    truckHireRate: number;
}

interface JobItem {
    id: number;
    typeId: number;
    type: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    hirePeriod: number;
    unitNumber: string; //if site address has a unit number
    amountQuoted: number;
    amountQuotedFormatted: string;
    quantityQuoted: number;
    quantityTotal: number;
    quantity: number;
    quantityScheduled: number;
    quantityInvoiced: number;       //used for dunny fencing as they may not invoice exactly what was installed on site
    quantityType: string;
    isVoid: boolean;
    voidSize: number;
    size: number;
    sizeQuoted: number;
    extraHours: number;
    extraHoursQuoted: number;
    confinedSpace: boolean;
    confinedSpaceQuoted: boolean;
    initialAmount: number;
    truckHireAmount: number;
    confinedSpaceAmount: number;
    sampleTestingAmount: number;
    disposalAmount: number;
    fuelSurchargeAmount: number;
    calloutFee: number;
    travelAmount: number;
    progressInvoiceAmount: number;      //need to deduct from total
    progressInvoiceAmountFormatted: string;
    price: number;
    priceDifference: number;
    priceDifferenceFormatted: string;
    overhireRate: number;
    vehicleItemOrder: number;
    isKwikstage: boolean;
    scheduleNotes: string;
    driverNotes: string;
    deleted: boolean;
    kwikstageItems: KwikstageScheduleItem[];
}

interface KwikstageScheduleItem {
    scheduleKwikstageId: number;
    jobQuoteItemCalculationId: number;
    starterBayId: number;
    starterBay: string;
    liftId: number;
    lift: string;
    baysTotal: number;
    baysComplete: number;
}

interface ItemOptionList {
    id: number;
    name: string;
    unitOfMeasure: string;
    chargeGst: boolean;
    callout: boolean;
    priceListItem: boolean;
    priceListDefaultDays: number;
    isWalkWay: boolean;
    typeId: number;
    installOnly: boolean;
}

const initJob: Job = {
    runsheetId: 0,
    scheduleBranchId: 1,
    scheduleDate: "",
    scheduleDateFormatted: "",
    onHireDate: "",
    truck: "",
    truckId: -1,
    jobId: 0,
    siteAddress: "",
    customerName: "",
    customerAccountType: "",
    updateTypeId: 1,
    estimatedTime: "",
    departureTime: "",
    siteArrivalTime: "",
    downTime: "",
    siteCompletionTime: "",
    driverNotes: "",
    chargeCalloutFee: -1,
    noCalloutInvoiceNotes: "",
    calloutNotes: "",
    planChangesReason: "",
    officeNotes: "",
    truckHireRate: 0,
    items: []
};


const RunSheetDriverPlan = (props: RunSheetDriverPlanProps) => {
    const [loading, setLoading] = React.useState(true);
    const [itemPlanOptions, setItemPlanOptions] = React.useState<ItemOptionList[]>([]);
    const [job, setJob] = React.useState<Job>(initJob);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [photoData, setPhotoData] = React.useState({ showPhotos: false, photos: [] });
    const [photoIndex, setPhotoIndex] = React.useState(-1);
    const [showNotes, setShowNotes] = React.useState(false);
    const [scheduleId, setScheduleId] = React.useState(-1);
    const [jobIndex, setJobIndex] = React.useState(-1);
    const [priceChange, setPriceChange] = React.useState(false);

    React.useEffect(() => {
        //filter item options
        var items = props.itemOptions.filter(i => i.callout === false && i.typeId != 11 && i.typeId != 12 && i.id != 4);
        setItemPlanOptions(items);

        getData();
    }, [props.runsheetId]);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Runsheet/GetRunsheetPlanChangesJob?RunsheetId=' + props.runsheetId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                setJob(res.data);
                setLoading(false);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    React.useEffect(() => {
        if (priceChange) {
            calculateTotalPrice();
        }
    }, [priceChange]);


    const handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var error = errors;
        if (e.target.value) {
            delete error["times"];
            if (e.target.name === "planChangesReason") {
                delete error["reason"];
            }
            setErrors(error);
        }

        setJob(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    //initial hire, callout, travel etc where we just want to calc the total
    const onCellPriceChange = (e: any, itemIndex: number) => {
        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        let newItem: any = {};
        newItem = item;
        var value = e.target.value * 1;
        newItem[e.target.name] = value;

        setJobIndex(itemIndex);
        setPriceChange(true);
        setJob(prevState => ({ ...prevState, items: items }));
    }

    const onCellTotalPriceChange = (e: any, itemIndex: number) => {
        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        let newItem: any = {};
        newItem = item;
        var value = e.target.value * 1;
        newItem[e.target.name] = value;

        //calculate amount
        var initialAmount = item.price - item.calloutFee - item.travelAmount - item.confinedSpaceAmount - item.sampleTestingAmount - item.disposalAmount - item.fuelSurchargeAmount + item.progressInvoiceAmount;
        newItem.initialAmount = parseFloat(initialAmount.toFixed(2));

        //calculate price difference between quoted and final price
        var priceDifference = item.price - item.amountQuoted;
        var priceDifferenceFormatted = priceDifference.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
        newItem.priceDifference = priceDifference;
        newItem.priceDifferenceFormatted = priceDifferenceFormatted;

        setJob(prevState => ({ ...prevState, items: items }));
    }

    const onCellChange = (e: any, itemIndex: number) => {
        updateItemError(e.target.value);

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        let newItem: any = {};
        newItem = item;
        newItem[e.target.name] = e.target.value * 1;

        setJob(prevState => ({ ...prevState, items: items }));
    }

    const onCellItemChange = (e: any, itemIndex: number) => {
        updateItemError(e.target.value);

        let itemId = e.target.value * 1;

        var items = [...job.items];
        var item = items[itemIndex];
        item.itemId = itemId;

        //set item id, hire period, 
        var itemRow = props.itemOptions.find(i => i.id === itemId);
        if (itemRow) {
            item.item = itemRow.name;
            item.hirePeriod = itemRow.priceListDefaultDays;
            item.isVoid = itemRow.name.toLowerCase().indexOf("void") >= 0;
        }

        if (itemIndex === items.length - 1) {
            var blankItem = addBlankRow(item.vehicleItemOrder);
            items.push(blankItem);
        }

        setJob(prevState => ({ ...prevState, items: items }));

        if (item.quantityTotal > 0) {
            calcNewItemPrice(itemIndex, itemId, item.voidSize, item.quantityTotal);
        }
    }

    const calculateTotalPrice = () => {
        var items = [...job.items];
        var item = items[jobIndex];

        var totalAmount = item.initialAmount + item.truckHireAmount + item.confinedSpaceAmount + item.sampleTestingAmount + item.disposalAmount + item.calloutFee + item.travelAmount + item.fuelSurchargeAmount - item.progressInvoiceAmount;
        item.price = parseFloat(totalAmount.toFixed(2));
        
        //calculate price difference between quoted and final price
        var priceDifference = totalAmount - item.amountQuoted;
        var priceDifferenceFormatted = priceDifference.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
        item.priceDifference = priceDifference;
        item.priceDifferenceFormatted = priceDifferenceFormatted;

        setJob(prevState => ({ ...prevState, items: items }));
        setJobIndex(-1);
        setPriceChange(false);
    }

    const addBlankRow = (vehicleItemOrder: number) => {
        let jobItem: JobItem = {
            id: 0,
            typeId: -1,
            type: "",
            item: "",
            itemId: -1,
            itemTypeId: -1,
            hirePeriod: 0,
            unitNumber: "",
            amountQuoted: 0,
            amountQuotedFormatted: "",
            quantityQuoted: 0,
            quantityTotal: 0,
            quantityScheduled: 0,
            quantity: 0,
            quantityInvoiced: 0,
            quantityType: "",
            initialAmount: 0,
            truckHireAmount: 0,
            confinedSpaceAmount: 0,
            sampleTestingAmount: 0,
            disposalAmount: 0,
            fuelSurchargeAmount: 0,
            calloutFee: 0,
            travelAmount: 0,
            progressInvoiceAmount: 0,
            progressInvoiceAmountFormatted: "$0.00",
            price: 0,
            priceDifference: 0,
            priceDifferenceFormatted: "$0.00",
            overhireRate: 0,
            vehicleItemOrder: vehicleItemOrder,
            voidSize: 0,
            size: 0,
            sizeQuoted: 0,
            extraHours: 0,
            extraHoursQuoted: 0,
            confinedSpace: false,
            confinedSpaceQuoted: false,
            isVoid: false,
            isKwikstage: false,
            deleted: false,
            scheduleNotes: "",
            driverNotes: "",
            kwikstageItems: []
        }

        return jobItem;
    }

    const calcNewItemPrice = async (itemIndex: number, itemId: number, voidSize: number, quantity: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var items = [...job.items];
        var item = items[itemIndex];

        axios.get('api/Job/CalculatePlanChangesNewItemPrice?JobId=' + props.jobId + '&ItemId=' + itemId + '&VoidSize=' + voidSize + '&Quantity=' + quantity + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.amount > 0) {
                //only update the quote prices if there is a pricelist amount
                var price = res.data.amount;
                item.price = price;
                item.overhireRate = res.data.overhireRate;
                item.travelAmount = res.data.travelAmount;
                item.fuelSurchargeAmount = res.data.fuelSurchargeAmount;
                item.initialAmount = res.data.initialHire;

                //calculate price difference between quoted and final price
                var priceDifference = price - item.amountQuoted;
                var priceDifferenceFormatted = priceDifference.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
                item.priceDifference = priceDifference;
                item.priceDifferenceFormatted = priceDifferenceFormatted;
            }

            setJob(prevState => ({ ...prevState, items: items }));
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const onCellQtyTotalChange = (e: any, itemIndex: number) => {
        updateItemError(e.target.value);

        var qtyTotal = e.target.value * 1;

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        item.quantityTotal = qtyTotal;
        if (props.systemCompanyId === 2) {
            item.quantityInvoiced = qtyTotal;   //update invoice qty to match total as not used in Scaffold
        }

        if (itemIndex === items.length - 1) {
            var blankItem = addBlankRow(item.vehicleItemOrder);
            items.push(blankItem);
        }

        setJob(prevState => ({ ...prevState, items: items }));

        if (item.id === 0 && item.itemId > 0) {
            calcNewItemPrice(itemIndex, item.itemId, item.voidSize, qtyTotal);
        } else if (item.id > 0) {
            calcPrice(itemIndex, qtyTotal);
        }
    }

    const onCellTruckHoursChange = (e: any, itemIndex: number) => {
        updateItemError(e.target.value);

        var truckHours = e.target.value * 1;
        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        item.extraHours = truckHours;
        item.truckHireAmount = truckHours * job.truckHireRate;

        if (itemIndex === items.length - 1) {
            var blankItem = addBlankRow(item.vehicleItemOrder);
            items.push(blankItem);
        }

        setJob(prevState => ({ ...prevState, items: items }));
        setJobIndex(itemIndex);
        setPriceChange(true);
    }

    const onCellInvQtyChange = (e: any, itemIndex: number) => {
        updateItemError(e.target.value);

        var qtyInv = e.target.value * 1;

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        item.quantityInvoiced = qtyInv;

        if (itemIndex === items.length - 1) {
            var blankItem = addBlankRow(item.vehicleItemOrder);
            items.push(blankItem);
        }

        setJob(prevState => ({ ...prevState, items: items }));

        if (item.id === 0 && item.itemId > 0) {
            calcNewItemPrice(itemIndex, item.itemId, item.voidSize, qtyInv);
        } else if (item.id > 0) {
            calcPrice(itemIndex, qtyInv);
        }
    }

    const onCellVoidSizeChange = (e: any, itemIndex: number) => {
        updateItemError(e.target.value);

        var voidSize = e.target.value * 1;

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        item.voidSize = voidSize;

        if (itemIndex === items.length - 1) {
            var blankItem = addBlankRow(item.vehicleItemOrder);
            items.push(blankItem);
        }

        setJob(prevState => ({ ...prevState, items: items }));

        if (item.id === 0 && item.itemId > 0) {
            calcNewItemPrice(itemIndex, item.itemId, voidSize, item.quantityTotal);
        };
    }

    const updateItemError = (value: number) => {
        var error = errors;
        if (value) {
            delete error["items"];
            setErrors(error);
        }
    }

    const calcPrice = async (itemIndex: number, quantity: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];

        axios.get('api/Job/CalculatePlanChangesPrice?JobId=' + props.jobId + '&ScheduleId=' + item.id + '&Quantity=' + quantity + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.amount > 0) {
                //only update the quote prices if there is a pricelist amount
                var price = res.data.amount + item.calloutFee;
                item.price = price;
                item.overhireRate = res.data.overhireRate;
                item.travelAmount = res.data.travelAmount;
                item.fuelSurchargeAmount = res.data.fuelSurchargeAmount;
                item.initialAmount = res.data.initialHire;

                //calculate price difference between quoted and final price
                var priceDifference = price - item.amountQuoted;
                var priceDifferenceFormatted = priceDifference.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
                item.priceDifference = priceDifference;
                item.priceDifferenceFormatted = priceDifferenceFormatted;
            }

            setJob(prevState => ({ ...prevState, items: items }));
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const onKsCellChange = (e: any, itemIndex: number, ksItemIndex: number) => {
        updateItemError(e.target.value);

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        var ksItem = item.kwikstageItems[ksItemIndex];
        let newItem: any = {};
        newItem = ksItem;
        newItem[e.target.name] = e.target.value;

        setJob(prevState => ({ ...prevState, items: items }));
    }

    const onKsCellQtyTotalChange = (e: any, itemIndex: number, ksItemIndex: number) => {
        updateItemError(e.target.value);

        var qtyTotal = e.target.value * 1;
        calcKsPrice(itemIndex, ksItemIndex, qtyTotal);
    }

    const calcKsPrice = async (itemIndex: number, ksItemIndex: number, quantity: number) => {
        const token = await authService.getAccessToken();

        //update item that has changed
        var items = [...job.items];
        var item = items[itemIndex];
        var ksItems = item.kwikstageItems;
        var ksItem = ksItems[ksItemIndex];
        ksItem.baysTotal = quantity;

        var calcKsPrice = {
            scheduleId: item.id,
            ksItems: ksItems
        };

        axios.post('api/Job/CalculatePlanChangesKwikstagePrice', calcKsPrice, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.amount > 0) {
                //only update the quote prices if there is a pricelist amount
                item.price = res.data.amount;
                item.overhireRate = res.data.overhireRate;
            }

            setJob(prevState => ({ ...prevState, items: items }));
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //CANCEL
    const cancelItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        var items = [...job.items];
        var item = items[itemIndex];

        item.deleted = true;
        item.quantityTotal = 0;
        item.quantityScheduled = 0;
        item.quantity = 0;

        setJob(prevState => ({ ...prevState, items: items }));
    }
    //END CANCEL

    //PHOTOS
    const viewPhotos = (e: any, runsheetId: number) => {
        e.preventDefault();
        getPhotos(runsheetId);
    }

    const getPhotos = async (runsheetId: number) => {
        const token = await authService.getAccessToken();

        axios.get('api/RunSheet/GetPhotos?RunsheetId=' + runsheetId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setPhotoData({ showPhotos: true, photos: res.data });
            setPhotoIndex(0);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const updatePhotoIndex = (newIndex: number) => {
        setPhotoIndex(newIndex);
    }

    const hidePhotos = () => {
        setPhotoIndex(-1);
        setPhotoData({ photos: [], showPhotos: false });
    }
    //END PHOTOS

    //NOTES
    const hideNotes = () => {
        setShowNotes(false);
        setScheduleId(-1);
        setJobIndex(-1);
    }

    const openNotes = (e: any, itemIndex: number) => {
        e.preventDefault();
        var item = job.items[itemIndex];

        setShowNotes(true);
        setScheduleId(item.id);
        setJobIndex(itemIndex);
    }

    const saveNotes = (scheduleNotes: string, timestamp: Uint8Array) => {
        var jobDetails = job;
        var item = jobDetails.items[jobIndex];
        item.scheduleNotes = scheduleNotes;

        toast.success("Notes Saved");

        setShowNotes(false);
        setScheduleId(-1);
        setJobIndex(-1);
        setJob(jobDetails);
    }
    //END NOTES

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        var errorStringTimes = "";
        var error = false;

        //Check Times
        if (props.systemCompanyId === 2) {
            if (job.departureTime == "") {
                errorStringTimes += "Departure Time, ";
                formIsValid = false;
            }
            if (job.siteArrivalTime == "") {
                errorStringTimes += "Site Arrival Time, ";
                formIsValid = false;
            }
            if (job.downTime && !DateHelpers.validTimeAmount(job.downTime)) {
                errorStringTimes += "Valid Down Time, ";
                formIsValid = false;
            }
            if (job.siteCompletionTime == "") {
                errorStringTimes += "Site Completion Time, ";
                formIsValid = false;
            }

            if (job.departureTime != "" && job.siteArrivalTime != "") {
                if (DateHelpers.validTimeAmount(job.siteArrivalTime) && DateHelpers.validTimeAmount(job.departureTime)) {
                    if (!DateHelpers.compareTimeGreater(job.departureTime, job.siteArrivalTime)) {
                        errorStringTimes += "Arrival Time needs to be > than Departure Time \n";
                    }
                } else {
                    errorStringTimes += "Departure Time and Arrival Time need to be valid times \n";
                }
            }

            if (job.siteCompletionTime != "" && job.siteArrivalTime != "") {
                if (DateHelpers.validTimeAmount(job.siteArrivalTime) && DateHelpers.validTimeAmount(job.siteCompletionTime)) {
                    if (!DateHelpers.compareTimeGreater(job.siteArrivalTime, job.siteCompletionTime)) {
                        errorStringTimes += "Completion Time needs to be > than Arrival Time \n";
                    }
                } else {
                    errorStringTimes += "Arrival Time and Completion Time need to be valid times \n";
                }
            }

            if (errorStringTimes != "") {
                formIsValid = false;
                errorList["times"] = "Please enter the following times: " + errorStringTimes.substring(0, errorStringTimes.length - 2);
            }
        }

        if (job.planChangesReason === "") {
            formIsValid = false;
            errorList["reason"] = "Reason for change is required";
        }

        //Check Quantity and Price
        var errorString = "Please enter the following values: ";
        var rowNumber = 0;
        let items = job.items;

        var activeItems = items.filter(i => !i.deleted);
        if (activeItems.length < 2) {
            formIsValid = false;
            errorList["itemList"] = "At least one Item is required";
        }

        for (var i = 0; i < items.length - 1; i++) {
            let fields = items[i];
            rowNumber += 1;
            var newErrorString = "";

            if (!fields.deleted) {
                if (fields.itemId < 0) {
                    error = true;
                    newErrorString += "Item, ";
                }

                //Check Non-Kwikstage Items first
                if (!fields.isKwikstage) {
                    //check normal quantity
                    let quantityTotal = fields.quantityTotal * 1;
                    if (quantityTotal < 1) {
                        error = true;
                        newErrorString += "Quantity Total, ";
                    }

                    let quantity = fields.quantity * 1;
                    if (quantity < 1) {
                        error = true;
                        newErrorString += "Quantity, ";
                    }

                    if (quantity > quantityTotal) {
                        error = true;
                        newErrorString += "Quantity must be <= Quantity Total, ";
                    }

                    let quantityInvoiced = fields.quantityInvoiced * 1;
                    if (quantityInvoiced < 1) {
                        error = true;
                        newErrorString += "Invoice Quantity, ";
                    }
                }

                //Check Void Size
                if (fields.isVoid && fields.voidSize <= 0) {
                    error = true;
                    newErrorString += "Void Size, ";
                }

                //Check Prices
                if (fields.price < 0.01) {
                    error = true;
                    newErrorString += "Price, ";
                }

                //overhire rate can be $0 for install only items
                var installOnly = false;
                if (fields.itemId > 0) {
                    //find item option
                    var itemRow = props.itemOptions.find(i => i.id === fields.itemId);
                    if (itemRow) {
                        installOnly = itemRow.installOnly;
                    }
                }

                if (fields.overhireRate < 0.01 && !installOnly) {
                    error = true;
                    newErrorString += "Overhire Rate, ";
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                }

                //Check Kwikstage Items (as these are on the next row/s)
                if (fields.isKwikstage) {
                    //check kwikstage quantities
                    for (var k = 0; k < fields.kwikstageItems.length; k++) {
                        rowNumber += 1;
                        var newErrorString = "";
                        var field = fields.kwikstageItems[k];

                        let baysTotal = field.baysTotal * 1;
                        if (baysTotal < 1) {
                            newErrorString += "Bays Total, ";
                            error = true;
                        }

                        let baysComplete = field.baysComplete * 1;
                        if (baysComplete < 1) {
                            newErrorString += "Bays Complete, ";
                            error = true;
                        }

                        if (baysComplete > baysTotal) {
                            newErrorString += "Bays Complete must be <= Bays Total, ";
                            error = true;
                        }

                        if (newErrorString != "") {
                            errorString += "\n"
                            errorString += "Row " + rowNumber + ": " + newErrorString;
                        }
                    }
                }
            }
        }
        if (error) {
            formIsValid = false;
            errorString = errorString.substring(0, errorString.length - 2);
            errorList["items"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = () => {
        if (validate()) {
            savePlanChanges();
        } else {
            toast.error("Please fix the validation errors on screen before saving!");
        }
    }

    const savePlanChanges = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var planItems = [...job.items];
        planItems.pop();

        var planChange = {
            scheduleBranchId: job.scheduleBranchId,
            scheduleDate: job.scheduleDate,
            onHireDate: job.onHireDate,
            runsheetId: job.runsheetId,
            jobId: job.jobId,
            truck: job.truck,
            truckId: job.truckId,
            departureTime: job.departureTime,
            siteArrivalTime: job.siteArrivalTime,
            downTime: job.downTime,
            siteCompletionTime: job.siteCompletionTime,
            planChangesReason: job.planChangesReason,
            items: planItems,
            subId: user.sub
        };

        //save plan change updates - price / quantity
        axios.post('api/RunSheet/SaveRunsheetPlanChanges', planChange, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Plan Changes Saved");
            props.save();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    let editNotes = <EditNoteModal systemCompanyId={props.systemCompanyId} scheduleId={scheduleId} editDriverNotes={false} showDriverScheduleNotes={true} showEditNotesModal={showNotes} save={saveNotes} hideModal={hideNotes} />
    let photos = <PhotoViewerModal showModal={photoData.showPhotos} photoIndex={photoIndex} photos={photoData.photos} hideModal={hidePhotos} update={updatePhotoIndex} />
    let lastRow = job.items.length - 1;
    let hasEpaOtherItems = job.items.filter(j => j.itemTypeId === 61).length > 0;

    const renderDetails = (
        <div>
            <div className="static-modal">
                {photos}
                {editNotes}
            </div>
            <div className="marginLeft10">
                <h5>
                    <span>{job.truck}</span>
                    <Link className={job.jobId === 0 ? "hidden" : "marginLeft20"} to={'/job/' + job.jobId} target="_blank">
                        {job.jobId}
                    </Link>
                    <span className="marginLeft20">{job.siteAddress} - {job.customerName} - {job.customerAccountType}</span>
                </h5>
            </div>
            <div className="marginLeft10 marginBottom10">
                <a className={props.systemCompanyId === 2 ? "makeitgreen marginRight10" : "hidden"} href="#" onClick={(e) => viewPhotos(e, job.runsheetId)}>
                    <span className="fas fa-file-image edit--icon"></span>
                </a>
                {job.driverNotes}
            </div>
            <div className={props.systemCompanyId === 3 ? "" : "hidden"}>
                <div><span className="label--inline-sm">Qty Original:</span> The original quantity that was quoted to the customer</div>
                <div><span className="label--inline-sm">Qty Total:</span> The total amount that was installed on site</div>
                <div><span className="label--inline-sm">Actual Qty:</span> The total amount that was installed on site by this truck</div>
                <div><span className="label--inline-sm">Invoice Qty:</span> The quantity that we are charging the customer for</div >
            </div>
            <div className={props.systemCompanyId === 3 ? "hidden" : "" }>
                <label className="input-group--inline" htmlFor="estimatedTime">
                    <span className="label-small">Est. Time:</span>
                    <span className="marginLeft10" id="estimatedTime">{job.estimatedTime}</span>
                </label>
                <label className="input-group--inline" htmlFor="departureTime">
                    <span className="label-small">Departure:</span>
                    <input className="marginLeft10" id="departureTime" name="departureTime" type="time" value={job.departureTime} onChange={(e) => handleChange(e)} />
                </label>
                <label className="input-group--inline" htmlFor="siteArrivalTime">
                    <span className="label-small">Arrival:</span>
                    <input className="marginLeft10" id="siteArrivalTime" name="siteArrivalTime" type="time" value={job.siteArrivalTime} onChange={(e) => handleChange(e)} />
                </label>
                <label className="input-group--inline" htmlFor="downTime">
                    <span className="label-small">Down Time:</span>
                    <input className="form-control runsheet__timeSize marginLeft10" type='text' id="downTime" name="downTime" pattern="(0?[0-9]|1[0-2]):[0-5][0-9]$" value={job.downTime} onChange={(e) => handleChange(e)} />
                </label>
                <label className="input-group--inline" htmlFor="siteCompletionTime">
                    <span className="label-small">Completion:</span>
                    <input className="marginLeft10" id="siteCompletionTime" name="siteCompletionTime" type="time" value={job.siteCompletionTime} onChange={(e) => handleChange(e)} />
                </label>
            </div>
            <div className={errors["times"] ? "" : "hidden"}><span className="label errors">{errors["times"]}</span></div>
            <div className="overflowAuto">
                <table className={'runsheetDriverTable ' + (props.systemCompanyId === 2 ? 'runsheetDriverTable--width' : 'runsheetDriverTable--dunnywidth') + ' table tableColours-header'}>
                    <thead>
                        <tr>
                            <th className="hidden">Id</th>
                            <th>Unit</th>
                            <th>Item</th>
                            <th className={props.systemCompanyId === 2 ? "" : "hidden"} >Void Size</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"} >Quoted Amount</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"} >Difference</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"} >Qty Original</th>
                            <th className="textalignright">Qty Total</th>
                            <th className="textalignright">{props.systemCompanyId === 3 ? "Actual" : ""} Qty</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"} >Invoice Qty</th>
                            <th className={props.systemCompanyId === 3 && hasEpaOtherItems ? "" : "hidden"}>Truck Hours</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"} >Initial</th>
                            <th className={props.systemCompanyId === 3 && hasEpaOtherItems ? "" : "hidden"}>Truck Hire</th>
                            <th className={props.systemCompanyId === 3 && hasEpaOtherItems ? "" : "hidden"}>Confined Space</th>
                            <th className={props.systemCompanyId === 3 && hasEpaOtherItems ? "" : "hidden"}>Sample Testing</th>
                            <th className={props.systemCompanyId === 3 && hasEpaOtherItems ? "" : "hidden"}>Disposal</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"}>Callout</th>
                            <th className={props.systemCompanyId === 3 ? "" : "hidden"}>Travel</th>
                            <th className={props.systemCompanyId === 3 && hasEpaOtherItems ? "" : "hidden"}>Progress Payments</th>
                            <th>Fuel Surcharge</th>
                            <th className="textalignright">Total Price</th>
                            <th className="textalignright">Overhire Rate</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {job.items.map((item, itemIndex) =>
                            <React.Fragment key={itemIndex}>
                                <tr className={item.deleted ? "hidden" : "runsheetRow"}>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{item.unitNumber === "" ? "" : "Unit " + item.unitNumber}</td>
                                    <td className="table__text--align table--col-l">
                                        <select className={item.kwikstageItems.length > 0 || item.id > 0 ? "hidden" : "select table__select--size"} id="itemId" name="itemId" value={item.itemId} onChange={(e) => onCellItemChange(e, itemIndex)} disabled={item.id > 0} >
                                            <option hidden defaultValue="-1"></option>
                                            {itemPlanOptions.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                        { item.kwikstageItems.length > 0 || item.id > 0 ? item.item : "" }
                                    </td>
                                    <td className={props.systemCompanyId === 2 ? "table__text--align table--col-m" : "hidden"}>
                                        <input type='number' min="0" step="1" className={item.isVoid ? "form-control textalignright" : "hidden"} id="voidSize" name="voidSize" value={item.voidSize} onChange={(e) => onCellVoidSizeChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        {item.amountQuotedFormatted}
                                    </td>
                                    <td className={props.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        {item.priceDifferenceFormatted}
                                    </td>
                                    <td className={props.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        {item.quantityQuoted}
                                    </td>
                                    <td className="table__text--align table--col-m">
                                        <input type='number' min="0" step="1" className={item.isKwikstage ? "hidden" : "form-control textalignright table-input--minwidth-small"} id="quantityTotal" name="quantityTotal" value={item.quantityTotal} onChange={(e) => onCellQtyTotalChange(e, itemIndex)} disabled={props.systemCompanyId === 3 && item.id > 0} />
                                    </td>
                                    <td className="table__text--align table--col-m">
                                        <input type='number' min="0" step="1" className={item.isKwikstage ? "hidden" : "form-control textalignright table-input--minwidth-small"} id="quantity" name="quantity" value={item.quantity} onChange={(e) => onCellChange(e, itemIndex)} disabled={props.systemCompanyId === 3 && item.id > 0} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 ? "table__text--align table--col-m" : "hidden"}>
                                        <input type='number' min="0" step="1" className="form-control textalignright table-input--minwidth-small" id="quantityInvoiced" name="quantityInvoiced" value={item.quantityInvoiced} onChange={(e) => onCellInvQtyChange(e, itemIndex)} disabled={item.itemTypeId === 61} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 && hasEpaOtherItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="1" className="form-control textalignright table-input--minwidth-small" id="extraHours" name="extraHours" value={item.extraHours} onChange={(e) => onCellTruckHoursChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 ?"table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="initialAmount" name="initialAmount" value={item.initialAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 && hasEpaOtherItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="truckHireAmount" name="truckHireAmount" value={item.truckHireAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 && hasEpaOtherItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="confinedSpaceAmount" name="confinedSpaceAmount" value={item.confinedSpaceAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 && hasEpaOtherItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="sampleTestingAmount" name="sampleTestingAmount" value={item.sampleTestingAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 && hasEpaOtherItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="disposalAmount" name="disposalAmount" value={item.disposalAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="calloutFee" name="calloutFee" value={item.calloutFee} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="travelAmount" name="travelAmount" value={item.travelAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className={props.systemCompanyId === 3 && hasEpaOtherItems ? "table__text--align" : "hidden"}>
                                        {item.progressInvoiceAmountFormatted}
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="fuelSurchargeAmount" name="fuelSurchargeAmount" value={item.fuelSurchargeAmount} onChange={(e) => onCellPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className="form-control runsheetDriverTable--pricewidth textalignright" id="price" name="price" value={item.price} onChange={(e) => onCellTotalPriceChange(e, itemIndex)} />
                                    </td>
                                    <td className="table__text--align table--col-m">
                                        <input type="number" min="0" step="0.01" className="form-control textalignright table-input--minwidth" id="overhireRate" name="overhireRate" value={item.overhireRate} onChange={(e) => onCellChange(e, itemIndex)} disabled={item.itemTypeId > 57 && item.itemTypeId < 62 } />
                                    </td>
                                    <td className="table__text--align table--col-m">
                                        <a className={itemIndex === lastRow ? "hidden" : item.scheduleNotes === "" && item.driverNotes === "" ? "tooltipitem tooltipitem__left alignIconCenter" : "makeitred tooltipitem tooltipitem__left alignIconCenter"} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => openNotes(e, itemIndex)}>
                                            <span className="fas fa-pencil-alt copy--icon"></span>
                                            <span className={item.scheduleNotes === "" && item.driverNotes === "" ? "hidden" : "tooltipitemtext tooltipitemtext__large tooltipitemtext__left"}>{item.scheduleNotes + item.driverNotes}</span>
                                        </a>
                                    </td>
                                    <td className="table__text--align table--col-m">
                                        {itemIndex != lastRow ?
                                            <div className="delete--tablecell">
                                                <a className="makeitred" href="#" onClick={(e) => cancelItem(e, itemIndex)}>
                                                    <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                </tr >
                                <tr className={item.itemTypeId > 57 && item.itemTypeId < 62 ? "runsheetRow" : "hidden"}>
                                    <td colSpan={hasEpaOtherItems ? 22 : 15}>
                                        <div className="makeitflexcenter alignitemscentre">
                                            <span>Total Litres: {item.size} (Quoted: {item.sizeQuoted})</span>
                                            <span className="marginLeft10 marginRight10">{(item.itemTypeId === 61 ? "Truck" : "Extra")} Hours: {item.extraHours} (Quoted: {item.extraHoursQuoted})</span>

                                            <span className={item.itemTypeId === 61 ? "" : "hidden"}>Confined Space?</span>
                                            <input type='checkbox' className={item.itemTypeId === 61 ? "input-control marginLeft10" : "hidden"} checked={item.confinedSpace} readOnly disabled></input>
                                            <span className={item.itemTypeId === 61 ? "marginLeft10" : "hidden"}>(Quoted)</span>
                                            <input type='checkbox' className={item.itemTypeId === 61 ? "input-control marginLeft10" : "hidden"} checked={item.confinedSpaceQuoted} readOnly disabled></input>
                                        </div>
                                    </td>
                                </tr>
                                <tr className={item.kwikstageItems.length > 0 ? "runsheetRow" : "hidden"}>
                                    <td colSpan={9}>
                                        <table>
                                            <tbody>
                                                {item.kwikstageItems.map((ksItem, ksIndex) =>
                                                    <tr key={itemIndex} className={item.typeId === 1 ? "tableColours--install" : item.typeId === 2 ? "tableColours--pickup" : item.typeId === 3 ? "tableColours--assist" : "tableColours--cleaning"}>
                                                        <td className="hidden">{ksItem.scheduleKwikstageId}</td>
                                                        <td className="table__text--align">{ksItem.starterBay} - {ksItem.lift}</td>
                                                        <td className="table__text--align table--col-m">
                                                            <input type='number' min="0" step="1" className="form-control textalignright" id="baysTotal" name="baysTotal" value={ksItem.baysTotal} onChange={(e) => onKsCellQtyTotalChange(e, itemIndex, ksIndex)} />
                                                        </td>
                                                        <td className="table__text--align table--col-m">
                                                            <input type='number' min="0" step="1" className="form-control textalignright" id="baysComplete" name="baysComplete" value={ksItem.baysComplete} onChange={(e) => onKsCellChange(e, itemIndex, ksIndex)} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </React.Fragment>
                        )}       
                    </tbody>
                        
                </table>
                {errors["items"] ?
                    (errors["items"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
                <div className={errors["itemList"] ? "" : "hidden"}><span className="label errors">{errors["itemList"]}</span></div>
            </div>
            <div>
                <label className="input-group--nowrap input-group--nowrap__max--width" htmlFor="planChangesReason">
                    <span className="label">Reason for change</span>
                    <textarea className="input allocate__teamnotes" id="planChangesReason" name="planChangesReason" value={job.planChangesReason} onChange={(e) => handleChange(e)}></textarea>
                </label>
                <span className="label errors">{errors["reason"]}</span>
            </div>
            <button className="defaultbutton-next" type="button" onClick={save}>Save</button>
        </div>


    );

    let contents = loading
        ? ""
        : renderDetails;

    return (<div>
        {contents}
    </div>)

};

export default RunSheetDriverPlan;

